import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useApi } from '@repo-level/core-front';

import { Button } from 'components/buttons';
import { Modal } from 'components/Modal';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { Spinner } from 'components/Spinner';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { PaymentMethodContext } from '../../providers';
import { deleteSavedPaymentMethod } from 'helpers/payments';
import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';
import { AccountTitleType, formatAccountTitle } from '../../helpers';

import { ScConfirmRemoveMethodModal } from './styled';

interface ConfirmRemoveMethodModalProps {
  currency: string;
  onClose: () => void;
}

export const ConfirmRemoveMethodModal: FC<ConfirmRemoveMethodModalProps> = ({
  onClose,
  currency,
}) => {
  const { t } = useTranslation(['bonuses', 'deposits']);

  const { isMobileS } = useContext(CommonUtilsContext);

  const {
    paymentMethod,
    currentPrevUsedProfile,
    setCurrentPaymentMethod,
  } = useContext(PaymentMethodContext);

  const [isMethodRemoved, setIsMethodRemoved] = useState(false);

  const [requestDeleteSavedMethod, { pending }] = useApi(
    deleteSavedPaymentMethod
  );

  const accountTitleFormated = useMemo(() => {
    if (!currentPrevUsedProfile?.id) return;

    return formatAccountTitle(currentPrevUsedProfile.title);
  }, [currentPrevUsedProfile]);

  const handleDeleteSavedMethod = useCallback(async () => {
    try {
      if (!paymentMethod?.id || !currentPrevUsedProfile?.id) return;

      const { response } = await requestDeleteSavedMethod({
        id: paymentMethod.id,
        currency,
        savedProfileId: currentPrevUsedProfile.id,
      }).request;

      if (response?.data) {
        showTooltip({
          type: TooltipTypes.ERROR,
          title: 'Oops! Something went wrong',
        });
      } else {
        setIsMethodRemoved(true);
      }
    } catch {}
  }, [
    paymentMethod.id,
    currentPrevUsedProfile?.id,
    requestDeleteSavedMethod,
    currency,
  ]);

  const handleClose = useCallback(() => {
    if (isMethodRemoved) {
      setCurrentPaymentMethod &&
        setCurrentPaymentMethod({
          ...paymentMethod,
          savedProfiles: paymentMethod.savedProfiles.filter(
            ({ id }) => id !== currentPrevUsedProfile?.id
          ),
        });
    }
    onClose();
  }, [
    currentPrevUsedProfile?.id,
    isMethodRemoved,
    onClose,
    paymentMethod,
    setCurrentPaymentMethod,
  ]);

  const modalSettings: ModalTemplateSettings = useMemo(() => {
    return isMobileS
      ? { type: 'mobile', mode: { type: 'default' } }
      : {
          type: 'desktop',
          size: 'small',
          mode: { type: 'default' },
        };
  }, [isMobileS]);

  return (
    <Modal>
      <ModalTemplate onClose={handleClose} settings={modalSettings}>
        <ScConfirmRemoveMethodModal>
          {pending ? (
            <Spinner size={30} center />
          ) : isMethodRemoved ? (
            <div className="content">
              <h3>{t('deposits:removeModal.successTitle')}</h3>
              <p>
                <Trans
                  i18nKey={
                    accountTitleFormated?.type === AccountTitleType.CREDIT_CARD
                      ? 'deposits:removeModal.successCreditCard'
                      : 'deposits:removeModal.success'
                  }
                  components={{
                    b: <b />,
                    account: accountTitleFormated?.title,
                  }}
                />
              </p>
            </div>
          ) : (
            <>
              <div className="content">
                <h3>{t('deposits:removeModal.title')}</h3>
                <p>
                  <Trans
                    i18nKey={
                      AccountTitleType.CREDIT_CARD
                        ? 'deposits:removeModal.textCreditCard'
                        : 'deposits:removeModal.text'
                    }
                    components={{
                      b: <b />,
                      account: accountTitleFormated?.title,
                    }}
                  />
                </p>
              </div>

              <footer>
                <Button
                  wide
                  autoPaddingHorizontal
                  xLarge
                  whiteTransparent
                  onClick={handleDeleteSavedMethod}
                >
                  {t('bonuses:cancelBonusModal.yes')}
                </Button>
                <Button
                  wide
                  autoPaddingHorizontal
                  xLarge
                  green
                  onClick={onClose}
                >
                  {t('bonuses:cancelBonusModal.no')}
                </Button>
              </footer>
            </>
          )}
        </ScConfirmRemoveMethodModal>
      </ModalTemplate>
    </Modal>
  );
};
